/**
 * React Hook for ConnectWise Integration
 * Manages ConnectWise SDK initialization and ticket data retrieval
 */

import { useState, useEffect } from 'react';
import { ConnectWiseSDK } from '../services/connectwise-sdk';
import { ConnectWiseAPI } from '../services/connectwise-api';
import { API_CONFIG } from '../config/constants';

// Check if running in demo/local mode
const isDemoMode = () => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

// Mock ticket data for demo mode
const DEMO_TICKET = {
  id: 12345,
  summary: 'Demo Ticket - User cannot access email on laptop',
  status: 'In Progress',
  priority: 'High',
  type: 'Service Request',
  company: 'Acme Corporation',
  contact: 'John Smith',
  description: 'User reports that Outlook is showing "Disconnected" status and cannot send or receive emails. Issue started this morning after Windows update. User has tried restarting the computer but the problem persists.'
};

export function useConnectWise() {
  const [ticketId, setTicketId] = useState(null);
  const [screenType, setScreenType] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sdk, setSdk] = useState(null);
  const [connectWiseUser, setConnectWiseUser] = useState(null);

  useEffect(() => {
    // Demo mode for local development
    if (isDemoMode()) {
      console.log('[Pod] Running in DEMO MODE (localhost detected)');
      setTicketId(DEMO_TICKET.id);
      setScreenType('ticket');
      setTicketData(DEMO_TICKET);
      setLoading(false);
      return;
    }

    console.log('[Pod] Initializing ConnectWise integration...');
    console.log('[Pod] ConnectWise Origin:', API_CONFIG.connectwiseOrigin);
    console.log('[Pod] Window location:', window.location.href);
    console.log('[Pod] ConnectWise SDK available:', !!window.ConnectWiseHostedAPI);

    // Set a timeout to show error if SDK doesn't initialize
    const timeoutId = setTimeout(() => {
      console.error('[Pod] Timeout: ConnectWise onLoad event never fired after 10 seconds');
      setError('ConnectWise SDK timeout. Check Origin configuration in ConnectWise Setup Tables.');
      setLoading(false);
    }, 10000);

    // SDK initialization with onLoad handler
    const cwSdk = new ConnectWiseSDK(
      API_CONFIG.connectwiseOrigin,
      async (data) => {
        clearTimeout(timeoutId); // Cancel timeout

        try {
          console.log('[Pod] ✅ ConnectWise onLoad event received!');
          console.log('[Pod] Data:', JSON.stringify(data, null, 2));

          const { screen, id, hostedAs } = data.screenObject;

          console.log('[Pod] Screen:', screen, 'ID:', id, 'HostedAs:', hostedAs);

          setScreenType(screen);

          // Only process ticket screens
          if (screen === 'ticket') {
            setTicketId(id);
            console.log('[Pod] Ticket ID set:', id);

            // Set minimal ticket data - the Copilot bot will fetch full details
            setTicketData({
              id: id,
              summary: `Ticket #${id}` // Placeholder, bot will get real data
            });

            // NEW: Get ConnectWise user information for audit tracking
            try {
              const authData = await cwSdk.getMemberAuthentication();
              console.log('[Pod] ConnectWise member info:', authData);

              setConnectWiseUser({
                userId: String(authData.memberid || 'unknown'),
                userEmail: authData.memberEmail || 'unknown@connectwise.local'
              });
            } catch (err) {
              console.warn('[Pod] Could not get member authentication:', err);
              // Set fallback values
              setConnectWiseUser({
                userId: 'connectwise-user',
                userEmail: 'unknown@connectwise.local'
              });
            }

            setLoading(false);
            console.log('[Pod] Ready to analyze ticket with Copilot bot');
          } else {
            // Not a ticket screen
            console.warn('[Pod] Not a ticket screen:', screen);
            setError(`This pod only works on ticket screens. Current screen: ${screen}`);
            setLoading(false);
          }
        } catch (err) {
          console.error('[Pod] ❌ Error in ConnectWise onLoad handler:', err);
          console.error('[Pod] Error details:', err.message, err.stack);
          setError(err.message);
          setLoading(false);
        }
      },
      true // Enable debug mode
    );

    setSdk(cwSdk);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  /**
   * Refresh ticket data
   * Since bot fetches the data, this just resets the state
   */
  const refreshTicket = async () => {
    if (!ticketId) return;

    console.log('[Pod] Refreshing ticket data for:', ticketId);
    // Just return the ticket ID - the bot will re-fetch everything
    setTicketData({
      id: ticketId,
      summary: `Ticket #${ticketId}`
    });
  };

  /**
   * Open a different ticket in ConnectWise
   */
  const openTicket = (newTicketId) => {
    if (sdk) {
      sdk.openScreen('ticket', newTicketId, true);
    }
  };

  /**
   * Refresh the current ConnectWise screen
   */
  const refreshScreen = () => {
    if (sdk) {
      sdk.refreshScreen();
    }
  };

  return {
    // State
    ticketId,
    screenType,
    ticketData,
    loading,
    error,
    connectWiseUser,

    // Methods
    refreshTicket,
    openTicket,
    refreshScreen,

    // SDK instance (for advanced usage)
    sdk
  };
}
